import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { useChatPolicy, useSettings, useUpdateChatPolicy } from '@/lib/queries'
import { PageHeader, Card, Callout, PageStack } from '@/components/ui/Page'
import { Button } from '@/components/ui/Button'
import { CheckboxField, TextAreaField, TextField } from '@/components/ui/Field'
import { useToast } from '@/components/ui/Toast'
import { ApiError } from '@/lib/api'
import { trimDecimals } from '@/lib/format'
import type { FreeChatMode, PlanExpiryBehaviour } from '@/types/api'
import styles from './Masters.module.css'

/**
 * The three free-chat behaviours, described by CONSEQUENCE.
 *
 * An admin choosing between these is deciding how the product makes money, so
 * each option says what a student experiences — not what the enum is called.
 */
const MODES: Array<{
  value: FreeChatMode
  label: string
  description: string
}> = [
  {
    value: 'PERMANENT',
    label: 'Always available',
    description:
      'Students can message any mentor at any time, with no limits. Most generous; no upsell pressure.',
  },
  {
    value: 'AFTER_SESSION',
    label: 'After a session is booked',
    description:
      'Chat unlocks once a student has a confirmed or completed session with that mentor. Limits below then apply.',
  },
  {
    value: 'DISABLED',
    label: 'Only with a booking',
    description:
      'No free messaging at all. A student must book a session before they can send anything.',
  },
]

/**
 * What happens to chat when a student's subscription runs out.
 *
 * *** THIS USED TO BE A HARDCODED RULE ***
 * The server fell through to the free-chat settings above whenever a student
 * had no live plan. That is a business decision — "an expired student keeps
 * whatever free chat this client offers" — and it belongs to the admin, not to
 * a line of code. Described by consequence, like the modes above.
 */
const EXPIRY_BEHAVIOURS: Array<{
  value: PlanExpiryBehaviour
  label: string
  description: string
}> = [
  {
    value: 'REQUIRE_PLAN',
    label: 'Chat stops until they renew',
    description:
      'Messaging ends the moment the plan does, even in conversations a past booking had unlocked. Students keep reading their history. Strongest reason to renew.',
  },
  {
    value: 'GRACE',
    label: 'Chat continues for a while',
    description:
      'Messaging keeps working for a set number of days after the plan ends, then stops. A softer nudge that gives students time to renew without losing contact.',
  },
  {
    value: 'KEEP_UNLOCKED',
    label: 'Keep existing conversations open',
    description:
      'Conversations a booking already unlocked stay open indefinitely, but no new ones can be started. Preserves relationships that already exist.',
  },
  {
    value: 'FALL_BACK',
    label: 'Treat them as a free user',
    description:
      'The free chat settings above apply again, exactly as for a student who never subscribed.',
  },
]

/**
 * A7 — Chat policy (the Chat Master).
 *
 * All four behaviours the stakeholder specified live here: the mode, how long
 * free chat lasts, the message and attachment quotas, and the wording shown
 * when a student runs out.
 *
 * *** THE UPSELL TEXT IS AUTHORED HERE, NOT IN THE APP ***
 * Whatever is typed below is exactly what a student reads when their chat
 * locks. The app renders it verbatim.
 */
export function ChatPolicyPage() {
  const toast = useToast()
  const { data, isLoading, error } = useChatPolicy()
  // Only consulted when the policy 404s — see `expiryApplies` below.
  const { data: settings } = useSettings()
  const updateMutation = useUpdateChatPolicy()

  const [mode, setMode] = useState<FreeChatMode>('AFTER_SESSION')
  // Controlled, not uncontrolled: the day count below only appears while this
  // is GRACE, so the form has to re-render when it changes.
  const [expiry, setExpiry] = useState<PlanExpiryBehaviour>('REQUIRE_PLAN')
  const [dirty, setDirty] = useState(false)

  // Seed local state once the policy arrives. `data?.free_chat_mode` as the
  // dependency, not `data`, so a background refetch returning an equal object
  // does not reset an edit in progress.
  useEffect(() => {
    if (data?.free_chat_mode) setMode(data.free_chat_mode)
  }, [data?.free_chat_mode])

  useEffect(() => {
    if (data?.plan_expiry_behaviour) setExpiry(data.plan_expiry_behaviour)
  }, [data?.plan_expiry_behaviour])

  // *** THE SERVER DECIDES THIS, NOT THE PORTAL ***
  // Asking what happens when a plan expires is meaningless where nothing is
  // sold by plan, so the whole section is hidden on a per-session client
  // rather than shown with an option that can never take effect.
  /*
    *** THE POLICY ANSWERS THIS, EXCEPT WHEN THERE IS NO POLICY ***
    `subscription_billing` rides along on the chat-policy response, so on a
    404 it is `undefined` — and a brand-new SUBSCRIPTION tenant would find
    the whole plan-expiry section missing, unable to say what happens when a
    student's plan lapses.

    Settings always answers, so it is the fallback for exactly that case.
  */
  const expiryApplies =
    data?.subscription_billing ??
    settings?.billing_mode === 'SUBSCRIPTION'

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const form = new FormData(event.currentTarget)

    const readNumber = (key: string): number | null => {
      const raw = String(form.get(key) ?? '').trim()
      // Empty means "no limit", which is meaningfully different from zero.
      return raw === '' ? null : Number(raw)
    }

    // *** SENDING GRACE WITH NO NUMBER WOULD SAVE A SETTING THAT DOES NOTHING ***
    // The server refuses it, but a 422 reads as a fault rather than as the
    // one question the admin still has to answer.
    const graceDays = readNumber('plan_expiry_grace_days')
    if (expiryApplies && expiry === 'GRACE' && !graceDays) {
      toast.error('How many days should chat continue after a plan ends?')
      return
    }

    try {
      await updateMutation.mutateAsync({
        free_chat_mode: mode,
        free_window_hours: readNumber('free_window_hours'),
        free_message_limit: readNumber('free_message_limit'),
        free_attachment_mb:
          String(form.get('free_attachment_mb') ?? '').trim() || null,
        unlock_requires_booking: form.get('unlock_requires_booking') === 'on',
        upsell_message: String(form.get('upsell_message') ?? '').trim() || null,
        // Omitted entirely on a per-session client: there is no plan to
        // expire, and the section that sets it is not on screen.
        ...(expiryApplies
          ? {
              plan_expiry_behaviour: expiry,
              plan_expiry_grace_days: expiry === 'GRACE' ? graceDays : null,
            }
          : {}),
      })
      toast.success('Chat policy saved.')
      setDirty(false)
    } catch (caught) {
      toast.error(
        caught instanceof ApiError ? caught.message : 'Could not save the policy.',
      )
    }
  }

  if (isLoading) {
    return (
      <>
        <PageHeader title="Chat policy" />
        <Card>
          <p style={{ color: 'var(--text-muted)' }}>Loading…</p>
        </Card>
      </>
    )
  }

  /*
    *** A 404 MEANS "NOT SET UP YET", NOT "SOMETHING BROKE" ***

    `GET /admin/chat-policy` 404s when a tenant has no policy row, which is
    the state EVERY new client starts in. This page used to treat any error
    the same way and render the message instead of the form — so the only
    route to creating a policy was a form that appeared only once a policy
    already existed. A brand-new tenant could never get out of it.

    Found on Sure60's first sign-in (2026-09-19): "No chat policy is
    configured yet." in red, with nothing on the page to configure it.

    The server has always supported this: `PUT /admin/chat-policy` upserts.
    Only the portal was missing the empty state.
  */
  const notConfiguredYet = error instanceof ApiError && error.status === 404

  if (error && !notConfiguredYet) {
    return (
      <>
        <PageHeader title="Chat policy" />
        <Card>
          <p style={{ color: 'var(--danger)' }}>{error.message}</p>
        </Card>
      </>
    )
  }

  // The quota fields only mean anything in AFTER_SESSION mode: PERMANENT has no
  // limits to set, and DISABLED has no free chat to limit.
  const quotasApply = mode === 'AFTER_SESSION'

  return (
    <PageStack>
      <PageHeader
        title="Chat policy"
        description="Controls when students can message mentors, and what they see when free messaging runs out."
      />

      {notConfiguredYet && (
        <Callout tone="info" title="No chat policy set yet">
          <p>
            Nothing has been configured for this institute, so the defaults
            below apply. Review them and save to make the policy explicit.
          </p>
        </Callout>
      )}

      <Card>
        <form onSubmit={handleSubmit} className={styles.policyForm} onChange={() => setDirty(true)}>
          <div className={styles.policySection}>
            <div>
              <p className={styles.policySectionTitle}>
                When can students message a mentor?
              </p>
              <p className={styles.policySectionHint}>
                Mentors can always reply — this limits students only.
              </p>
            </div>

            <div className={styles.modeOptions}>
              {MODES.map((option) => (
                <label key={option.value} className={styles.modeOption}>
                  <input
                    type="radio"
                    name="free_chat_mode"
                    value={option.value}
                    checked={mode === option.value}
                    onChange={() => {
                      setMode(option.value)
                      setDirty(true)
                    }}
                    className={styles.modeRadio}
                  />
                  <span>
                    <span className={styles.modeLabel}>{option.label}</span>
                    <span className={styles.modeDescription}>
                      {option.description}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          {quotasApply && (
            <div className={styles.policySection}>
              <div>
                <p className={styles.policySectionTitle}>Free chat limits</p>
                <p className={styles.policySectionHint}>
                  Leave a field empty for no limit. Reaching any limit locks the
                  conversation and shows the message below.
                </p>
              </div>

              <div className={styles.formRow}>
                <TextField
                  label="Free period"
                  name="free_window_hours"
                  type="number"
                  min={1}
                  defaultValue={data?.free_window_hours ?? ''}
                  hint="Hours after the first message."
                  placeholder="No time limit"
                />

                <TextField
                  label="Message limit"
                  name="free_message_limit"
                  type="number"
                  min={1}
                  defaultValue={data?.free_message_limit ?? ''}
                  hint="Messages the student may send."
                  placeholder="No limit"
                />
              </div>

              <TextField
                label="Attachment allowance"
                name="free_attachment_mb"
                type="number"
                min={0}
                step="0.1"
                // Trailing zeros trimmed: the server stores DECIMAL, so this
                // arrives as "5.00" and an editable field showing that invites
                // the reader to wonder what the decimals are for.
                defaultValue={trimDecimals(data?.free_attachment_mb)}
                hint="Total megabytes of files the student may send in one conversation."
                placeholder="No limit"
              />
            </div>
          )}

          <div className={styles.policySection}>
            <div>
              <p className={styles.policySectionTitle}>Booking unlock</p>
            </div>

            <CheckboxField
              label="A confirmed booking unlocks that conversation permanently"
              name="unlock_requires_booking"
              defaultChecked={data?.unlock_requires_booking ?? true}
              hint="Once a student books, limits stop applying to that mentor. Recommended — a paying student expecting to message their mentor and finding it locked is a support call."
            />
          </div>

          {expiryApplies && (
            <div className={styles.policySection}>
              <div>
                <p className={styles.policySectionTitle}>
                  When a student&rsquo;s plan expires
                </p>
                <p className={styles.policySectionHint}>
                  Applies to students whose subscription has ended or run out
                  of sessions. Reading past messages is never affected — this
                  controls sending only. Individual plans can override this.
                </p>
              </div>

              <div className={styles.modeOptions}>
                {EXPIRY_BEHAVIOURS.map((option) => (
                  <label key={option.value} className={styles.modeOption}>
                    <input
                      type="radio"
                      name="plan_expiry_behaviour"
                      value={option.value}
                      checked={expiry === option.value}
                      onChange={() => {
                        setExpiry(option.value)
                        setDirty(true)
                      }}
                      className={styles.modeRadio}
                    />
                    <span>
                      <span className={styles.modeLabel}>{option.label}</span>
                      <span className={styles.modeDescription}>
                        {option.description}
                      </span>
                    </span>
                  </label>
                ))}
              </div>

              {expiry === 'GRACE' && (
                <TextField
                  label="Grace period"
                  name="plan_expiry_grace_days"
                  type="number"
                  min={1}
                  max={365}
                  required
                  defaultValue={data?.plan_expiry_grace_days ?? ''}
                  hint="Days after the plan ends that a student can still send messages."
                  placeholder="e.g. 7"
                />
              )}

              {expiry === 'REQUIRE_PLAN' && (
                <Callout tone="warning" title="This overrides the booking unlock above">
                  <p>
                    A student whose plan has ended loses messaging even in
                    conversations a past booking unlocked. That is what makes
                    this the strongest reason to renew — but a long-standing
                    student will notice it. They can still read everything.
                  </p>
                </Callout>
              )}
            </div>
          )}

          <div className={styles.policySection}>
            <div>
              <p className={styles.policySectionTitle}>
                What students see when chat locks
              </p>
              <p className={styles.policySectionHint}>
                Shown exactly as written, in the app, where the message box would
                be.
              </p>
            </div>

            <TextAreaField
              label="Message"
              name="upsell_message"
              rows={3}
              maxLength={500}
              defaultValue={data?.upsell_message ?? ''}
              placeholder="Book a session with this mentor to keep chatting."
            />
          </div>

          {mode === 'DISABLED' && (
            <Callout tone="warning" title="Students cannot start a conversation">
              <p>
                With this setting, a student who has not booked sees only the
                message above. Make sure it explains what to do next.
              </p>
            </Callout>
          )}

          <div className={styles.saveBar}>
            {dirty && (
              <span className={styles.savedNote}>You have unsaved changes.</span>
            )}
            <Button
              type="submit"
              variant="primary"
              loading={updateMutation.isPending}
              disabled={!dirty}
            >
              Save policy
            </Button>
          </div>
        </form>
      </Card>
    </PageStack>
  )
}
